import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Ein Filterzustand, der in der URL steht — und deshalb „Zurück“ übersteht.
 *
 * Als `useState` ging er bei jedem Ausflug in eine Detailseite verloren: wer in
 * der Freigaben-Seite auf „Abgeschlossen“ stand, einen Lead öffnete und
 * zurückging, landete wieder auf „Laufend“. Der Browser stellt beim Zurückgehen
 * die URL wieder her, nicht den Komponentenzustand.
 *
 * Anders als `useQuerySeededState` ist die URL hier die einzige Wahrheit, in
 * beide Richtungen — dasselbe Muster, das die Leads-Seite für `?liste=` schon
 * hatte. Geschrieben wird mit `replace`, sonst sammelte jede Änderung im
 * Auswahlfeld einen Verlaufseintrag, und „Zurück“ klapperte durch alte
 * Filterstände statt zur vorigen Seite. Die Vorgabe steht nicht in der URL,
 * damit die Adresse ohne Filter sauber bleibt.
 *
 * `erlaubt` fängt handgeschriebene oder veraltete Werte ab: ein unbekannter
 * Reiter fällt auf die Vorgabe zurück, statt eine leere Seite zu zeigen.
 */
export function useUrlState<T extends string>(
  parameter: string,
  vorgabe: T,
  erlaubt?: readonly T[],
): [T, (wert: T) => void] {
  const [suchparameter, setSuchparameter] = useSearchParams();
  const roh = suchparameter.get(parameter);
  const wert =
    roh !== null && (!erlaubt || (erlaubt as readonly string[]).includes(roh))
      ? (roh as T)
      : vorgabe;

  const setzen = useCallback(
    (neu: T) => {
      setSuchparameter(
        (bisher) => {
          const naechste = new URLSearchParams(bisher);
          if (neu === vorgabe) naechste.delete(parameter);
          else naechste.set(parameter, neu);
          return naechste;
        },
        { replace: true },
      );
    },
    [parameter, vorgabe, setSuchparameter],
  );

  return [wert, setzen];
}
